import { AppNode } from "@features/builder/graph/types"
import { getDefaultValues } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/getDefaultValues"
import VirtualizedFormRows from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/VirtualizedFormRows"
import { getValidationSchema } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/schema"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  CircularProgress,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react"
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form"
import * as yup from "yup"
import CheckboxSelectAll from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/CheckboxSelectAll"
import { ReplaceField } from "@features/builder/BulkNodesEditDialog/FieldsRadioGroup"
import { enqueueSnackbar } from "notistack"
import { BULK_NODES_EDIT_DIALOG_ACTIONS_ID } from "@features/builder/BulkNodesEditDialog/BulkNodesEditDialog"
import { createPortal } from "react-dom"
import NodeLinkDialog from "@components/NodeLink/NodeLinkDialog"
import {
  COLUMN_WIDTH,
  STICKY_COLUMN_LEFT,
  getStickyColumnSx,
} from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/stickyColumns"

export const BULK_NODES_EDIT_FORM_ID = "bulk-nodes-edit-form"

export type BulkNodesEditFormValues = yup.InferType<ReturnType<typeof getValidationSchema>>

type BulkNodesEditFormProps = {
  onSubmit: SubmitHandler<BulkNodesEditFormValues>
  onSubmitError: SubmitErrorHandler<BulkNodesEditFormValues>
  nodes: AppNode[]
  disabled: boolean
}

type ResetFormOptions = {
  keepDirtyChanges: boolean
  nodes: AppNode[]
}

export type BulkNodesEditFormRef = {
  applyReplace: (searchValue: string, replaceValue: string, field: ReplaceField) => void
  reset: (options: ResetFormOptions) => void
}

const FORM_FIELD_BY_REPLACE_FIELD: Record<
  ReplaceField,
  keyof BulkNodesEditFormValues["nodes"][string]
> = {
  label: "label",
  "unit suffix": "unitSuffix",
}

const BulkNodesEditForm = forwardRef<BulkNodesEditFormRef, BulkNodesEditFormProps>((props, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const validationSchema = useMemo(() => getValidationSchema(props.nodes), [props.nodes])
  const defaultValues = useMemo(() => getDefaultValues(props.nodes), [props.nodes])

  const formMethods = useForm<BulkNodesEditFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
    disabled: props.disabled,
  })

  const handleApplyReplace = useCallback(
    (searchValue: string, replaceValue: string, field: ReplaceField) => {
      if (!searchValue) return

      const targetField = FORM_FIELD_BY_REPLACE_FIELD[field]
      if (!targetField) return

      let updatedRowsCount = 0
      props.nodes.forEach((node) => {
        const fieldName = `nodes.${node.id}.${targetField}` as const
        const currentValue = formMethods.getValues(fieldName) ?? ""
        if (typeof currentValue !== "string") return

        const updatedValue = currentValue.replaceAll(searchValue, replaceValue)
        if (updatedValue === currentValue) return

        formMethods.setValue(fieldName, updatedValue, { shouldDirty: true })
        updatedRowsCount++
      })

      enqueueSnackbar({
        message: `${updatedRowsCount} rows updated`,
        variant: "info",
      })
    },
    [formMethods, props.nodes],
  )

  const handleReset = useCallback(
    ({ keepDirtyChanges, nodes }: ResetFormOptions) => {
      let updatedDefaultValues = getDefaultValues(nodes)

      // preserve changes if node is still present in the new list of nodes
      if (keepDirtyChanges) {
        const currentRowsById = formMethods.getValues("nodes")
        updatedDefaultValues = {
          nodes: Object.fromEntries(
            Object.entries(updatedDefaultValues.nodes).map(([nodeId, defaultRow]) => [
              nodeId,
              currentRowsById?.[nodeId] ?? defaultRow,
            ]),
          ),
        }
      }

      formMethods.reset(updatedDefaultValues, { keepDefaultValues: keepDirtyChanges })
    },
    [formMethods],
  )

  // allow parent component to call callbacks that should have direct impact on the form state
  useImperativeHandle(
    ref,
    () => ({
      applyReplace: handleApplyReplace,
      reset: handleReset,
    }),
    [handleApplyReplace, handleReset],
  )

  const handleSaveLink = (newLink: string, node: AppNode | undefined) => {
    if (!node) return

    formMethods.setValue(`nodes.${node.id}.link`, newLink, { shouldDirty: true })
  }

  const dialogActionsContainer = document.getElementById(BULK_NODES_EDIT_DIALOG_ACTIONS_ID)

  return (
    <FormProvider {...formMethods}>
      {dialogActionsContainer &&
        createPortal(
          <Button
            form={BULK_NODES_EDIT_FORM_ID}
            type="submit"
            variant="contained"
            disabled={props.disabled || formMethods.formState.isSubmitting}
            endIcon={formMethods.formState.isSubmitting && <CircularProgress size={16} />}
          >
            Save selected nodes
          </Button>,
          dialogActionsContainer,
        )}

      <form
        autoComplete="off"
        onSubmit={formMethods.handleSubmit(props.onSubmit, props.onSubmitError)}
        id={BULK_NODES_EDIT_FORM_ID}
      >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            overflowX: "initial",
            overflowY: "auto",
            maxHeight: "calc(100dvh - 250px)",
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            {/* enforces exact, stable column widths so sticky columns don't shrink while scrolling */}
            <colgroup>
              <col style={{ width: COLUMN_WIDTH.numericOrder }} />
              <col style={{ width: COLUMN_WIDTH.isSelected }} />
              <col style={{ width: COLUMN_WIDTH.label }} />
              <col style={{ width: COLUMN_WIDTH.type }} />
              <col style={{ width: COLUMN_WIDTH.link }} />
              <col style={{ width: COLUMN_WIDTH.unitShowAsPercentage }} />
              <col style={{ width: COLUMN_WIDTH.unitDecimalPlaces }} />
              <col style={{ width: COLUMN_WIDTH.unitSuffix }} />
              <col style={{ width: COLUMN_WIDTH.historicMnemonic }} />
              <col style={{ width: COLUMN_WIDTH.scenarioMnemonic }} />
              <col style={{ width: COLUMN_WIDTH.forecastMnemonic }} />
              <col style={{ width: COLUMN_WIDTH.tags }} />
              <col style={{ width: COLUMN_WIDTH.graphTags }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ ".MuiTableCell-root": { px: 1, py: 0, wordBreak: "break-word" } }}>
                <TableCell
                  sx={getStickyColumnSx(STICKY_COLUMN_LEFT.numericOrder, { isHeader: true })}
                >
                  <strong>No.</strong>
                </TableCell>
                <TableCell
                  align="center"
                  sx={getStickyColumnSx(STICKY_COLUMN_LEFT.isSelected, { isHeader: true })}
                >
                  <CheckboxSelectAll />
                </TableCell>
                <TableCell sx={getStickyColumnSx(STICKY_COLUMN_LEFT.label, { isHeader: true })}>
                  <strong>Label</strong>
                </TableCell>
                <TableCell>
                  <strong>Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Link</strong>
                </TableCell>
                <TableCell>
                  <strong>
                    [Unit] <br /> Show as %
                  </strong>
                </TableCell>
                <TableCell>
                  <strong>
                    [Unit] <br /> Decimal places
                  </strong>
                </TableCell>
                <TableCell>
                  <strong>
                    [Unit] <br /> Suffix
                  </strong>
                </TableCell>
                <TableCell>
                  <strong>Historic mnemonic</strong>
                </TableCell>
                <TableCell>
                  <strong>Scenario mnemonic</strong>
                </TableCell>
                <TableCell>
                  <strong>Forecast mnemonic</strong>
                </TableCell>
                <TableCell>
                  <strong>Node-level tags</strong>
                </TableCell>
                <TableCell>
                  <strong>Graph-level tags</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <VirtualizedFormRows nodes={props.nodes} tableContainerRef={tableContainerRef} />
            <NodeLinkDialog onSave={handleSaveLink} />
          </Table>
        </TableContainer>
      </form>
    </FormProvider>
  )
})

export default BulkNodesEditForm

BulkNodesEditForm.displayName = "BulkNodesEditForm"
